import React, {
  ElementType,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import Menu from "@mui/material/Menu";
import Button from "@mui/material/Button";
import { Box, Stack, SvgIcon, Typography, useMediaQuery } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { isHtmlViewAtom, shouldCloseMenuAtom } from "atoms";
import { useAtom, useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import {
  CustomDialog,
  CustomTooltip,
} from "@web-applications/allinga-web-components";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { SSMLTag, TTSCustomEditorColors } from "shared/constants";
import { useSSMLTagRemover } from "shared/hooks/useSSMLTag";
import { ReactComponent as ClearTagsIcon } from "assets/icons/clear-tags-icon.svg";
import {
  getToolbarElementColor,
  getChevronColor,
} from "shared/utils/tts-editor-helper";

// "componentName" is used to generate the id for the button and menu (for testing purposes)
// which is used for the aria-controls and aria-labelledby attributes respectively
// and menuTitle cannot be used for this purpose because it contains translations
interface CustomIconTextButtonMenuProps {
  componentId: string;
  menuTitle: string;
  children: React.ReactNode;
  showChevronIcon?: boolean;
  useDialog?: boolean;
  dialogHeader?: string;
  onDialogPrimaryButtonClick?: VoidFunction;
  isDialogPrimaryButtonDisabled?: boolean;
  onDialogClose?: VoidFunction;
  onDialogOpen?: VoidFunction;
  isActive?: boolean;
  isEditorEmpty?: boolean;
  shouldBeDisabled?: boolean;
  footerText?: React.ReactNode;
  showClearButton?: boolean;
  editorRef: RefObject<EditorRef | null>;
  tagType?: SSMLTag;
  icon?: ElementType;
  width?: string;
  isSelectionDisabled?: boolean;
}

function CustomIconTextButtonMenu({
  componentId,
  menuTitle,
  children,
  showChevronIcon,
  useDialog,
  dialogHeader,
  onDialogPrimaryButtonClick,
  isDialogPrimaryButtonDisabled,
  onDialogClose,
  onDialogOpen,
  isActive = false,
  isEditorEmpty = false,
  shouldBeDisabled = false,
  footerText,
  showClearButton,
  editorRef,
  tagType,
  icon,
  width,
  isSelectionDisabled = false,
}: CustomIconTextButtonMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [shouldCloseMenu, setShouldCloseMenu] = useAtom(shouldCloseMenuAtom);
  const [isHovered, setIsHovered] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const isHtmlView = useAtomValue(isHtmlViewAtom);
  const removeSSMLTag = useSSMLTagRemover(editorRef);
  const isVerySmallScreen = useMediaQuery("(max-width:800px)");
  const isSmallScreen = useMediaQuery("(max-width:1535px)");
  const showLabels = !isSmallScreen;

  useEffect(() => {
    if (shouldCloseMenu) {
      setOpen(false);
      setShouldCloseMenu(false);
    }
  }, [shouldCloseMenu, setShouldCloseMenu]);

  const handleClose = () => {
    setOpen(false);
    onDialogClose?.();
  };

  const handleClick = () => {
    onDialogOpen?.();
    setOpen(true);
  };

  const handleClear = () => {
    if (showClearButton && removeSSMLTag && tagType) {
      if (tagType === SSMLTag.Prosody) {
        // Explicitly remove all prosody-related tags
        removeSSMLTag(SSMLTag.Rate);
        removeSSMLTag(SSMLTag.Duration);
        removeSSMLTag(SSMLTag.Volume);
        removeSSMLTag(SSMLTag.Pitch);
        removeSSMLTag(SSMLTag.Contour);
      }
      removeSSMLTag(tagType);
    }
    handleClose();
  };

  const buttonContent = (
    <Stack direction="row" alignItems="center">
      <Stack direction="row" gap={0.5} alignItems="center">
        <SvgIcon
          sx={{
            color: getToolbarElementColor(
              isHtmlView,
              componentId === "break-pause" ? false : isEditorEmpty,
              isHovered,
              isActive,
              shouldBeDisabled
            ),
            fontSize: "small",
            verticalAlign: "middle",
            width: "20px",
            height: "20px",
          }}
          component={icon!}
          inheritViewBox
        />

        {showLabels && (
          <Typography
            variant="subtitle2"
            sx={{
              color: getToolbarElementColor(
                isHtmlView,
                componentId === "break-pause" ? false : isEditorEmpty, // For Break menu, treat as not empty to show enabled color
                isHovered,
                isActive,
                shouldBeDisabled
              ),
            }}
          >
            {menuTitle}
          </Typography>
        )}
      </Stack>
      {showChevronIcon && showLabels && (
        <Box sx={{ ml: 1, display: "flex", alignItems: "center" }}>
          {open ? (
            <KeyboardArrowUpIcon
              sx={{
                width: 20,
                height: 20,
                color: getChevronColor(
                  isHtmlView,
                  componentId === "break-pause" ? false : isEditorEmpty,
                  shouldBeDisabled
                ),
              }}
            />
          ) : (
            <KeyboardArrowDownIcon
              sx={{
                width: 20,
                height: 20,
                color: getChevronColor(
                  isHtmlView,
                  componentId === "break-pause" ? false : isEditorEmpty,
                  shouldBeDisabled
                ),
              }}
            />
          )}
        </Box>
      )}
    </Stack>
  );

  const isBreakMenu = componentId === "break-pause";
  const button = (
    <Button
      ref={buttonRef}
      id={`${componentId}-button`}
      aria-controls={
        open ? `${componentId}-${useDialog ? "dialog" : "menu"}` : undefined
      }
      aria-haspopup="true"
      aria-expanded={open ? "true" : undefined}
      onClick={handleClick}
      disableRipple
      disabled={
        isHtmlView || (!isBreakMenu && isEditorEmpty) || shouldBeDisabled
      }
      sx={{
        color: getToolbarElementColor(
          isHtmlView,
          isBreakMenu ? false : isEditorEmpty,
          false,
          isActive
        ),
        height: "32px",
        p: 1,
        marginX: 0.5,
        border: isActive
          ? `1px solid ${TTSCustomEditorColors.ToolbarActiveBorder}`
          : "transparent",
        backgroundColor: shouldBeDisabled
          ? "transparent"
          : isActive
            ? TTSCustomEditorColors.ToolbarActive
            : "transparent",
        "&:hover": {
          backgroundColor: shouldBeDisabled
            ? "transparent"
            : isActive
              ? TTSCustomEditorColors.ToolbarActive
              : "action.hover",
          color: "text.primary",
        },
        minWidth: !showLabels ? "32px" : isBreakMenu ? "fit-content" : "auto",
        pointerEvents: shouldBeDisabled ? "none" : "auto",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {buttonContent}
    </Button>
  );

  const getTooltipContent = () => {
    if (isBreakMenu && !showLabels) {
      return t("tts.toolbarMenu.breakMenu.title");
    }
    if (isSelectionDisabled) {
      return t("ssml.nesting.tooltip");
    }
    if (shouldBeDisabled) {
      return t("tts.toolbarMenu.disabledMenuTooltip");
    }
    if (!showLabels) {
      return menuTitle;
    }
    return "";
  };

  const shouldShowTooltip =
    isSelectionDisabled ||
    (shouldBeDisabled && !isEditorEmpty && !isHtmlView) ||
    isSmallScreen;

  const tooltipContent = getTooltipContent();

  const getTooltipPlacement = () => {
    if (isSelectionDisabled) {
      return "bottom";
    }
    return isVerySmallScreen ? "bottom" : "top";
  };

  return (
    <>
      {!showLabels ? (
        <>
          {shouldShowTooltip && tooltipContent ? (
            <CustomTooltip
              placement={getTooltipPlacement()}
              title={tooltipContent}
              disableHoverListener={false}
              forceShow
            >
              {button}
            </CustomTooltip>
          ) : (
            button
          )}
        </>
      ) : shouldShowTooltip && tooltipContent ? (
        <CustomTooltip
          placement={getTooltipPlacement()}
          title={tooltipContent}
          disableHoverListener={false}
          forceShow
        >
          {button}
        </CustomTooltip>
      ) : (
        button
      )}

      {useDialog ? (
        <CustomDialog
          id={`${componentId}-dialog`}
          open={open}
          handleOnClose={handleClose}
          showCloseIcon
          showDivider={false}
          header={dialogHeader}
          primaryButtonText={
            isActive ? t("buttons.update") : t("buttons.apply")
          }
          onPrimaryButtonClick={onDialogPrimaryButtonClick}
          maxWidth="md"
          height="auto"
          maxHeight={window.innerHeight - 32}
          isPrimaryButtonDisabled={isDialogPrimaryButtonDisabled}
          footerText={footerText}
          tertiaryButtonText={t("buttons.cancel")}
          onTertiaryButtonClick={handleClose}
          dialogActionsDivider
          {...(showClearButton && {
            secondaryButtonText: t("buttons.clearTags"),
            onSecondaryButtonClick: handleClear,
          })}
        >
          {children}
        </CustomDialog>
      ) : (
        <Menu
          id={`${componentId}-menu`}
          anchorEl={buttonRef.current}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": `${componentId}-button`,
          }}
          PaperProps={{
            sx: {
              boxShadow: "None",
              border: "1px solid #E0E0E0",
              borderRadius: "0",
              width,
            },
          }}
        >
          {children}
          {showClearButton && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "8px 16px",
              }}
            >
              <Button
                endIcon={<ClearTagsIcon />}
                onClick={handleClear}
                color="error"
                variant="outlined"
                size="large"
                fullWidth
              >
                {t("buttons.clearTags")}
              </Button>
            </Box>
          )}
        </Menu>
      )}
    </>
  );
}

export default CustomIconTextButtonMenu;
